import { twMerge } from "tailwind-merge";
import { useCallback } from "react";
import { m } from "framer-motion";
import IeltsInputResultIcon from "../ResultIcon";
import useIeltsTheme from "../../hooks/useIeltsTheme";

type TOption = {
  label: React.ReactNode;
  value: string;
  title: React.ReactNode;
  result?: boolean;
  readonly?: boolean;
};

type Props = {
  options: TOption[];
  value: string;
  className?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  readonly?: boolean;
  id?: string;
  useIndexLabel?: boolean;
};

function Option({
  value,
  label,
  title,
  checked,
  result,
  readonly,
  onClick,
}: {
  checked?: boolean;
  onClick?: () => void;
} & TOption) {
  const { theme } = useIeltsTheme();

  return (
    <div
      id="do-not-select"
      className={twMerge(
        "flex items-center w-fit gap-x-3 cursor-pointer relative",
        readonly ? "cursor-default" : "",
      )}
      onClick={onClick}
      role="button"
      onKeyDown={undefined}
      tabIndex={-1}
      key={value}
    >
      <div
        className={twMerge(
          "border-[1px] w-[26px] h-[26px] flex items-center justify-center rounded-full select-none shrink-0",
          "bg-ielts-control border-ielts-fg shadow-[0px_2px_0px_var(--ielts-fg-muted)]",
        )}
      >
        <span className="text-[13px] font-bold leading-[26px] text-ielts-fg">
          {label}
        </span>
      </div>
      <m.div
        whileTap={readonly ? {} : { scale: 0.8 }}
        className={twMerge(
          "w-[32px] h-[32px]  border-[2px] p-[3px] rounded-full select-none shrink-0",
          "border-ielts-line-strong hover:bg-ielts-surface-muted",
        )}
      >
        <div
          // CLASSIC_LIGHT_ORANGE filled the checked dot with its pale #DFCBA5
          // border colour rather than an accent, so the checked state barely
          // registered. `accent` is the affordance in every theme.
          style={{ backgroundColor: checked ? theme.accent : "" }}
          className={twMerge("w-full h-full rounded-full")}
        />
      </m.div>
      <div>{title}</div>

      <IeltsInputResultIcon
        className="absolute -top-[10px] -right-6"
        result={result}
      />
    </div>
  );
}

function IeltsRadioInput({
  id,
  className = "",
  options,
  value,
  disabled,
  onChange,
  readonly,
  useIndexLabel,
}: Props) {
  const handleClick = useCallback(
    (v: string) => {
      if (disabled) return;
      onChange?.(v);
    },
    [disabled, onChange],
  );

  return (
    <div id={id} className={twMerge("flex flex-col gap-y-3", className)}>
      {options.map((option, oIndex) => {
        const indexLabel = String.fromCharCode(65 + oIndex);
        return (
          <Option
            {...option}
            checked={value === option.value}
            key={option.value}
            label={useIndexLabel ? indexLabel : option.label}
            onClick={() => (readonly ? undefined : handleClick(option.value))}
            readonly={readonly}
          />
        );
      })}
    </div>
  );
}

export default IeltsRadioInput;
